import type { OrderAddOn } from '@libs/models';

/**
 * Provenance helpers for order add-ons.
 *
 * Add-ons selected during booking and add-ons an admin bolts onto a live order are
 * stored in the same `order.addOns` array, so the order detail page had no way to
 * tell them apart. `addedAt`/`addedByUserId` are stamped when an item is added
 * post-booking; orders written before that stamp existed fall back to the cart's
 * booking-time add-on list, which is the only other record of what was booked.
 */

/** Minimum shape needed to identify an add-on. */
export type IdentifiableAddOn = Pick<OrderAddOn, '_id' | 'isProduct'>;

/** Identity of an add-on within an order: the same product can appear as add-on and product. */
export const addOnIdentity = (addOn: IdentifiableAddOn) =>
  `${addOn._id}::${addOn.isProduct ? 'product' : 'add-on'}`;

/**
 * A consumable multiset of add-on identities.
 *
 * `take` matches one copy per call and then spends it, so a list holding the same item twice
 * matches twice and no more - which is what makes a quantity of two reconcile against a
 * quantity of one instead of both copies passing as the same match. Identity carries
 * `isProduct`, so a product and an add-on that happen to share an `_id` never stand in for
 * one another.
 */
export const createAddOnIdentityPool = (addOns: IdentifiableAddOn[]) => {
  const remaining = new Map<string, number>();

  addOns.forEach((addOn) => {
    const key = addOnIdentity(addOn);
    remaining.set(key, (remaining.get(key) || 0) + 1);
  });

  return {
    /** Spends one copy of this add-on's identity, or reports that none is left. */
    take: (addOn: IdentifiableAddOn): boolean => {
      const key = addOnIdentity(addOn);
      const count = remaining.get(key) || 0;

      if (!count) {
        return false;
      }

      remaining.set(key, count - 1);
      return true;
    },
  };
};

/**
 * Carries `addedAt`/`addedByUserId` across an add-on list update, stamping items that
 * are new to the order. Existing items keep whatever provenance they already had, so
 * removing one item never re-dates the others.
 *
 * Matching consumes each existing entry once, so a list that legitimately holds the
 * same item twice keeps one stamp per copy rather than treating the second as new.
 */
export const stampPostBookingAddOns = (
  existingAddOns: OrderAddOn[] | undefined,
  incomingAddOns: OrderAddOn[],
  addedByUserId: string | undefined,
  addedAt: Date,
): OrderAddOn[] => {
  const unmatched = new Map<string, OrderAddOn[]>();
  (existingAddOns || []).forEach((addOn) => {
    const key = addOnIdentity(addOn);
    unmatched.set(key, [...(unmatched.get(key) || []), addOn]);
  });

  return incomingAddOns.map((addOn) => {
    const queue = unmatched.get(addOnIdentity(addOn));
    const previous = queue?.shift();

    if (previous) {
      // Already on the order - preserve its provenance rather than restamping it.
      return {
        ...addOn,
        addedAt: previous.addedAt,
        addedByUserId: previous.addedByUserId,
      };
    }

    return { ...addOn, addedAt, addedByUserId };
  });
};

/** Minimum shape needed to decide provenance. Keeps the split usable from the admin
 *  client, where `addedAt` arrives as an ISO string rather than a `Date`. */
export type AddOnProvenance = IdentifiableAddOn & Pick<OrderAddOn, 'addedAt'>;

export interface SplitAddOns<T extends AddOnProvenance> {
  /** Selected during booking, or predating provenance tracking with no evidence otherwise. */
  atBooking: T[];
  /** Added to the order after it was placed. */
  addedAfterBooking: T[];
}

/**
 * Splits an order's add-ons into what was booked and what was added later.
 *
 * `bookedAddOns` is the add-on list from the cart's booking-time line items. It is what
 * lets pre-existing orders resolve correctly: an add-on with no `addedAt` that is also
 * absent from the booking list must have been added after the fact. When the booking
 * list is unavailable, unstamped add-ons are reported as booked rather than guessed at.
 *
 * Matching against that list consumes one booked copy per unstamped item, so an order
 * carrying a second copy of something booked once has that second copy resolve as the
 * post-booking addition it is. A stamped item never consumes a booked copy: its stamp
 * already proves it arrived later.
 */
export const splitAddOnsByProvenance = <T extends AddOnProvenance>(
  addOns: T[] | undefined,
  bookedAddOns?: IdentifiableAddOn[],
): SplitAddOns<T> => {
  const booked = bookedAddOns ? createAddOnIdentityPool(bookedAddOns) : undefined;

  return (addOns || []).reduce<SplitAddOns<T>>(
    (split, addOn) => {
      const addedAfterBooking = addOn.addedAt ? true : booked ? !booked.take(addOn) : false;

      split[addedAfterBooking ? 'addedAfterBooking' : 'atBooking'].push(addOn);
      return split;
    },
    { atBooking: [], addedAfterBooking: [] },
  );
};

/** Total of the add-ons added after booking, in cents. */
export const sumAddOnPriceInCents = (addOns: Pick<OrderAddOn, 'priceInCents'>[]): number =>
  addOns.reduce((total, addOn) => total + addOn.priceInCents, 0);

type BookedAddOnPrice = IdentifiableAddOn & {
  priceInCents?: number;
  adjustedPriceInCents?: number;
};

/** Booking-time price, including the legacy field used by older cart snapshots. */
export const bookedAddOnPriceInCents = (addOn: BookedAddOnPrice): number =>
  addOn.priceInCents || addOn.adjustedPriceInCents || 0;

/**
 * Pairs live add-ons with the booking snapshots they came from.
 *
 * One snapshot is spent per call, mirroring how `splitAddOnsByProvenance` consumes one booked
 * copy per unstamped add-on, so an order holding two copies of something booked twice pairs each
 * copy with its own snapshot. Identity carries `isProduct`, so a product never pairs with an
 * add-on that happens to share an `_id`. Whatever is left over was removed from the order.
 */
const createBookedAddOnQueue = (bookedAddOns: BookedAddOnPrice[]) => {
  const queues = new Map<string, BookedAddOnPrice[]>();

  bookedAddOns.forEach((addOn) => {
    const key = addOnIdentity(addOn);
    queues.set(key, [...(queues.get(key) || []), addOn]);
  });

  return {
    /** Spends the next booking snapshot for this add-on, or reports that none is left. */
    match: (addOn: IdentifiableAddOn): BookedAddOnPrice | undefined =>
      queues.get(addOnIdentity(addOn))?.shift(),
    /** The snapshots no live add-on claimed: the copies that are no longer on the order. */
    unmatched: (): BookedAddOnPrice[] => [...queues.values()].flat(),
  };
};

/**
 * Calculates the add-on delta used by order billing. Missing booking provenance is not treated
 * as proof that every live add-on is new or every unknown booked add-on was removed.
 *
 * Three things move the total: add-ons added after booking, booked add-ons that were removed,
 * and booked add-ons that were kept but repriced. `adminUpdateOrderAddOns` accepts a replacement
 * price for an item it also keeps stamped as booked, so a retained copy is not automatically
 * worth what it was booked at. The booked price is already inside the cart subtotal, so a
 * retained copy contributes only the difference between its live price and the snapshot it
 * paired with. A retained copy with no snapshot to compare against contributes nothing: its
 * booked price is unknown, not zero.
 */
export const calculateOrderAddOnAdjustmentInCents = (
  currentAddOns: OrderAddOn[] | undefined,
  bookedAddOns: BookedAddOnPrice[] | undefined,
): number => {
  const { atBooking, addedAfterBooking } = splitAddOnsByProvenance(currentAddOns, bookedAddOns);
  const booked = createBookedAddOnQueue(bookedAddOns || []);

  const retainedPriceDeltaInCents = atBooking.reduce((total, addOn) => {
    const snapshot = booked.match(addOn);

    return snapshot ? total + addOn.priceInCents - bookedAddOnPriceInCents(snapshot) : total;
  }, 0);

  const removedFromBookingInCents = booked
    .unmatched()
    .reduce((total, addOn) => total + bookedAddOnPriceInCents(addOn), 0);

  return (
    sumAddOnPriceInCents(addedAfterBooking) + retainedPriceDeltaInCents - removedFromBookingInCents
  );
};
